import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import {
  InvestigationSpine,
  type InvestigationTab,
} from '@/components/layouts/InvestigationSpine'
import { DetailActionBar } from '@/components/detail/DetailActionBar'
import { trackStoryOpened } from '@/lib/analytics-events'
import { ConfidenceBadge } from '@/components/stories/ConfidenceBadge'
import { VendorBadge, VendorStoryNote } from '@/components/vendor-content/VendorBadge'
import { TierBadge } from '@/components/stories/TierBadge'
import { SourcesModal } from '@/components/stories/SourcesModal'
import { StoryDetailSourcesSection } from '@/components/stories/StoryDetailSourcesSection'
import { DiscussCTA } from '@/components/community/DiscussCTA'
import { ChannelCTA } from '@/components/community/ChannelCTA'
import { LeakSiteActivityCallout } from '@/components/leak-sites/LeakSiteActivityCallout'
import { StorySupplyChainCallout } from '@/components/supply-chain/StorySupplyChainCallout'
import { EditorialCoiDisclosure } from '@/components/stories/EditorialCoiDisclosure'
import { BriefingContextPanel } from '@/components/stories/BriefingContextPanel'
import { Badge } from '@/components/ui/badge'
import { useStory } from '@/hooks/stories'
import { useBriefingStoryContext } from '@/hooks/briefing-story-context'
import { useAuth } from '@/contexts/AuthContext'

/**
 * Public detail view for a single story cluster.
 *
 * Reuses the SP3 `InvestigationSpine` layout: the left context strip holds
 * confidence + tier + publisher + metrics (sources, risk, severity, first
 * seen, last updated) and entity groups (CVEs, threat actors, vendors,
 * victims). The right workspace renders a Summary tab with the synthesised
 * summary and a button to open `SourcesModal`. The action bar reuses the
 * SP3 `DetailActionBar` (copy-link + watch toggle).
 *
 * Route registration for `/stories/:id` is deferred to T10.
 */
export function StoryDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const [watched, setWatched] = useState(false)
  const { data: story, isLoading, error } = useStory(id)
  const { user } = useAuth()

  // KAN-661 U5 (R14, R17, R31). A SEPARATE query from `useStory`, on a separate
  // key, over a separate authenticated POST — the ordinary story read stays
  // context-free and cacheable for every reader exactly as it was. Yields null
  // for anyone who did not arrive from a briefing link, which is almost
  // everyone, and the panel below then renders nothing.
  const { context: briefingContext } = useBriefingStoryContext({
    storyId: id,
    subject: user?.sub,
  })

  // KAN-32 story:opened — once per resolved story, not per render.
  useEffect(() => {
    if (story) trackStoryOpened(story)
  }, [story?.id])

  if (isLoading) {
    return (
      <p className="p-8 text-sm text-muted-foreground">Loading story…</p>
    )
  }

  if (error || !story) {
    return (
      <div className="p-8">
        <h1 className="text-lg font-semibold">Story not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          It may have been archived or merged.{' '}
          <Link to="/stories" className="text-primary hover:underline">
            Back to stories.
          </Link>
        </p>
      </div>
    )
  }

  const contextStrip = (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <ConfidenceBadge label={story.confidenceLabel} />
        <TierBadge tier={story.publisherBadge.tier} />
        <span className="text-sm font-medium">{story.publisherBadge.name}</span>
        {/* KAN-822 T9 — Vendor stories are labelled and explained (dark build flag). */}
        <VendorBadge story={story} />
      </div>
      <VendorStoryNote story={story} />

      <dl className="space-y-2 text-sm">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Sources</dt>
          <dd className="font-medium">
            {story.sourceCount} ({story.independentOrgs} orgs)
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Risk</dt>
          <dd className="font-medium">{story.maxRiskScore.toFixed(0)}</dd>
        </div>
        {story.maxSeverity && (
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Severity</dt>
            <dd className="font-medium uppercase">{story.maxSeverity}</dd>
          </div>
        )}
        <div className="flex justify-between">
          <dt className="text-muted-foreground">First seen</dt>
          <dd className="tabular-nums">
            {format(new Date(story.earliestPublishedAt), 'yyyy-MM-dd')}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Last updated</dt>
          <dd className="tabular-nums">
            {format(new Date(story.latestSourceAddedAt), 'yyyy-MM-dd')}
          </dd>
        </div>
      </dl>

      {story.primaryCves.length +
        story.primaryThreatActors.length +
        story.primaryVendors.length +
        (story.primaryPersons?.length ?? 0) +
        story.primaryVictims.length >
        0 && (
        <div className="space-y-2">
          <EntityGroup label="CVEs" items={story.primaryCves} />
          <EntityGroup label="Threat actors" items={story.primaryThreatActors} />
          <VendorEntityGroup label="Vendors" vendors={story.primaryVendors} />
          {story.primaryPersons && story.primaryPersons.length > 0 && (
            <EntityGroup
              label="People"
              items={story.primaryPersons.map((p) => p.displayName)}
            />
          )}
          <EntityGroup label="Victims" items={story.primaryVictims} />
        </div>
      )}

      {/* Plan B Plan 2 T19: related ransomware leak-site activity callout —
          renders inline below the threat-actor list per spec §5.5. Self-hides
          when the activity array is empty or absent. */}
      {story.relatedLeakSiteActivity && (
        <LeakSiteActivityCallout activity={story.relatedLeakSiteActivity} />
      )}
    </div>
  )

  const summaryContent = (
    <div className="space-y-4 p-6">
      <div className="entity-cta-row flex flex-wrap items-center gap-3">
        <DiscussCTA entityType="story" entityId={story.id} />
        <ChannelCTA ontologyTags={story.ontologyTags ?? {}} />
      </div>

      {/* Charter 23 T7 (KAN-150) — editorial conflict-of-interest disclosure.
          Deliberately ABOVE the summary: the reader is entitled to know about
          a commercial relationship before reading our assessment of the
          vendor, not after. Self-hides when story.coiDisclosure is null, which
          is the case for nearly every story. */}
      <EditorialCoiDisclosure
        disclosure={story.coiDisclosure}
        vendors={story.coiDisclosedVendors}
      />

      <article className="prose prose-sm dark:prose-invert max-w-none">
        <p>{story.synthesisedSummary}</p>
      </article>

      {/* KAN-661 U5 — briefing-click context (R22, R24). ADDITIVE and placed
          AFTER the synthesised summary on purpose: the reader's own relevance
          and our suggested actions are a reason to keep reading, never a
          substitute for the assessment itself or for the cited sources below.
          Self-hides for every reader who did not arrive from a briefing link. */}
      <BriefingContextPanel context={briefingContext} />

      {/* Plan C Plan 2 T21 — supply-chain provider callout. Self-hides
          when story.relatedSupplyChain is empty/undefined (the common
          case for non-cloud-vendor stories). */}
      <StorySupplyChainCallout entries={story.relatedSupplyChain} />

      {/* Inline sources section — promotes the per-article full-text reader
          (previously buried inside SourcesModal) to a primary CTA on the Story
          Detail page itself. The modal stays accessible via the "View all N
          sources" link inside the section for the multi-source aggregator UX. */}
      <StoryDetailSourcesSection
        storyId={story.id}
        totalSourceCount={story.sourceCount}
        onOpenFullSourcesModal={() => setSourcesOpen(true)}
      />
    </div>
  )

  const tabs: InvestigationTab[] = [
    {
      id: 'summary',
      label: 'Summary',
      content: summaryContent,
    },
  ]

  return (
    <>
      <InvestigationSpine
        title={story.title}
        contextStrip={contextStrip}
        actionBar={
          <DetailActionBar
            url={typeof window !== 'undefined' ? window.location.href : ''}
            watched={watched}
            onToggleWatch={setWatched}
            entity={{ type: 'Story', name: story.title }}
          />
        }
        tabs={tabs}
      />
      <SourcesModal
        storyId={story.id}
        open={sourcesOpen}
        onClose={() => setSourcesOpen(false)}
      />
    </>
  )
}

function EntityGroup({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {items.map((x) => (
          <Badge key={x} variant="outline" className="font-mono">
            {x}
          </Badge>
        ))}
      </div>
    </div>
  )
}

/**
 * Phase 2 vendor unification (Plan T11+T12) — render `primaryVendors:
 * VendorRef[]` as clickable links to `/vulnerabilities?vendor=<id>` (the
 * existing vendor-filter convention used by `AffectedVendorsBadges`).
 * Tier-1 vendors (`tier === 1`) get a small "T1" pill so the elevated
 * platform-vendor signal surfaces alongside the badge.
 */
function VendorEntityGroup({
  label,
  vendors,
}: {
  label: string
  vendors: import('../lib/types').VendorRef[]
}) {
  if (vendors.length === 0) return null
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div
        className="mt-1 flex flex-wrap gap-1.5"
        data-testid="story-primary-vendors"
      >
        {vendors.map((v) => (
          <Link
            key={v.id}
            to={`/vulnerabilities?vendor=${v.id}`}
            // R28: VENDOR NAME in an attribute.
            data-ph-no-capture
            aria-label={`Filter vulnerabilities by vendor ${v.name}`}
          >
            <Badge variant="outline" className="font-mono cursor-pointer">
              {v.name}
              {v.tier === 1 && (
                <span
                  className="ml-1 rounded-sm bg-primary/15 px-1 text-[10px] font-semibold uppercase tracking-wide text-primary"
                  data-testid="vendor-tier-1-pill"
                  aria-label="Tier 1 vendor"
                >
                  T1
                </span>
              )}
            </Badge>
          </Link>
        ))}
      </div>
    </div>
  )
}
